'use client';

import { User } from '@api/models';
import { BookOpen } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { ComponentType, ReactNode, useState } from 'react';

import { ConfirmDialog } from '@ui/components/common/dialog/confirmDialog';
import OnboardingLearner from '@ui/components/icons/OnboardingLearner';
import OnboardingMentor from '@ui/components/icons/OnboardingMentor';

type Mode = 'mentor' | 'learner';

interface ActionIconProps {
  className?: string;
}

/**
 * Brand-logo icon used next to "Passer en mentor" — rendered via a plain
 * <img> so the caller-supplied Tailwind `size-*` class fully drives the
 * rendered dimensions (next/image's intrinsic width/height can fight CSS).
 */
const MentorActionIcon: ComponentType<ActionIconProps> = ({ className }) => (
  // eslint-disable-next-line @next/next/no-img-element
  <img src="/logo-without-background.svg" alt="" aria-hidden="true" className={className} />
);

const LearnerActionIcon: ComponentType<ActionIconProps> = ({ className }) => <BookOpen className={className} />;

interface UseModeSwitcherOptions {
  /**
   * Optional same-origin path the user should land on *after* the mode flip.
   * Forwarded to `/auth/switch-mode` as `returnTo`. When omitted the server
   * picks the default landing page for the new mode (mentor → /dashboard,
   * learner → /).
   *
   * Use this for in-context flips like "Switch to learner to book this
   * activity" so the user comes back to the same page in the new mode
   * instead of being teleported home.
   */
  returnTo?: string;
}

interface UseModeSwitcherResult {
  /** Open the confirmation dialog for the opposite mode */
  open: () => void;
  /** Current mode ("mentor" or "learner") */
  currentMode: Mode;
  /**
   * Icon rendered next to the mode-switch menu entry. Represents the
   * **target** mode (where the user is going), which pairs naturally with
   * the verb-led `actionLabel` — e.g. the TopTopTime logo next to
   * "Passer en mentor", a book next to "Passer en apprenant".
   */
  ActionIcon: ComponentType<ActionIconProps>;
  /** Action label for the switch row (e.g. "Switch to learner") — frames the
   * menu entry as a verb-led action rather than an ambiguous status pair. */
  actionLabel: string;
  /** Whether a mode switch is in flight */
  isPending: boolean;
  /** Dialog that must be rendered somewhere in the tree for the switcher to work */
  dialogs: ReactNode;
}

/**
 * Shared learner/mentor mode switcher. Semantically this toggles which
 * experience the user is seeing — not a permanent profession change — so the
 * UI copy frames it as a mode switch.
 *
 * Because there are only two modes and the current one is already known, the
 * interaction is a single confirmation dialog (no intermediate selector).
 *
 * Callers render their own row styling and drop `dialogs` somewhere in the
 * tree. Safe to call with `user === undefined` (e.g. while `useCurrentUser()`
 * is still loading) — in that case `open()` is a no-op and `dialogs` is null.
 *
 * Implementation: confirming the dialog navigates to `/auth/switch-mode`, a
 * server route that updates the user, sets the `user_type` cookie, and
 * redirects to the appropriate landing page in a single hop. We deliberately
 * keep all of that off the client so the user sees one clean navigation
 * instead of "menu chrome flips on the current page → page navigates" — the
 * earlier flow updated the React Query cache before triggering a hard reload,
 * which made the transition feel jittery.
 *
 * Note: we intentionally do not surface a success toast on switch. The menu
 * icon/label flip on the destination page is signal enough and an extra
 * toast on every switch was noisy for users who toggle frequently.
 */
export function useModeSwitcher(user: User | undefined, options: UseModeSwitcherOptions = {}): UseModeSwitcherResult {
  const t = useTranslations('Profile');
  const { returnTo } = options;

  const currentMode: Mode = user?.isProfessional ? 'mentor' : 'learner';
  const targetMode: Mode = currentMode === 'mentor' ? 'learner' : 'mentor';

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // We don't get a network response back (we're navigating away), so
  // `isPending` is just "the dialog has been confirmed and the browser is
  // about to leave this page". It exists so callers can disable the trigger
  // and avoid double-clicks during the brief window before navigation kicks in.
  const [isSwitching, setIsSwitching] = useState(false);

  const open = () => {
    if (!user) return;
    setIsConfirmOpen(true);
  };

  const confirmSwitch = () => {
    if (!user) return;
    setIsConfirmOpen(false);
    setIsSwitching(true);
    // Hard navigation (not router.push) so the response's Set-Cookie for
    // user_type is honoured by the browser and middleware sees the new role
    // on the very next request.
    const url = new URL('/auth/switch-mode', window.location.origin);
    url.searchParams.set('mode', targetMode);
    if (returnTo) url.searchParams.set('returnTo', returnTo);
    window.location.assign(url.pathname + url.search);
  };

  const ActionIcon: ComponentType<ActionIconProps> = targetMode === 'mentor' ? MentorActionIcon : LearnerActionIcon;

  const actionLabel = targetMode === 'mentor' ? t('switchToMentor') : t('switchToLearner');

  const dialogs = !user ? null : (
    <ConfirmDialog
      isOpen={isConfirmOpen}
      onClose={() => setIsConfirmOpen(false)}
      onConfirm={confirmSwitch}
      onReject={() => setIsConfirmOpen(false)}
      title={targetMode === 'mentor' ? t('switchToMentorModeTitle') : t('switchToLearnerModeTitle')}
      description={targetMode === 'mentor' ? t('switchToMentorModeDescription') : t('switchToLearnerModeDescription')}
      confirmButtonText={t('switch')}
      rejectButtonText={t('cancel')}
      icon={
        targetMode === 'mentor' ? <OnboardingMentor className="size-20" /> : <OnboardingLearner className="size-20" />
      }
    />
  );

  return {
    open,
    currentMode,
    ActionIcon,
    actionLabel,
    isPending: isSwitching,
    dialogs
  };
}
